/* iter194 — Admin "Edit Requests" tab for timesheet corrections.
   ------------------------------------------------------------
   Shows Pending / Approved / Rejected filters. For each request displays
   employee, date, entry current values, requested values, reason category,
   reason text, submitted timestamp, and payroll week. Admin actions:
     - Approve → PUT /api/admin/time-edits/:id  (status='approved')
     - Decline → opens dialog to capture decline_reason (required)
   ------------------------------------------------------------ */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Clock, User, Wrench, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { fmtLocalTime } from '../lib/timeFmt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtT = (iso) => (iso ? fmtLocalTime(iso, { hour: '2-digit', minute: '2-digit', month: 'numeric', day: 'numeric' }) : '—');
const fmtDT = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const REASON_LABELS = {
  forgot_clock_in: 'Forgot to clock in',
  forgot_clock_out: 'Forgot to clock out',
  wrong_job: 'Clocked into wrong job',
  break_time_wrong: 'Break time wrong',
  app_phone_issue: 'App/phone issue',
  poor_service: 'Poor service',
  admin_asked: 'Admin asked me to submit correction',
  other: 'Other',
};

const StatusChip = ({ status }) => {
  const cls = status === 'approved'
    ? 'text-green-300 bg-green-500/15 border-green-500/40'
    : status === 'rejected'
      ? 'text-red-300 bg-red-500/15 border-red-500/40'
      : 'text-amber-200 bg-amber-500/15 border-amber-500/40';
  return (
    <span data-testid={`edit-req-status-${status}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded ${cls}`}>
      {status}
    </span>
  );
};

export const EditRequestsTab = ({ currentUserId }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  // iter211 — Repair tool: preview + backfill approved requests whose payroll
  // change never took effect (legacy approvals from before auto-materialize).
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairData, setRepairData] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairApplying, setRepairApplying] = useState(false);
  // iter212 — Edit Approved Request (correct a typo without losing audit).
  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctTarget, setCorrectTarget] = useState(null);
  const [correctForm, setCorrectForm] = useState({ clock_in: '', clock_out: '', break_hours: '', reason: '' });
  const [correctBusy, setCorrectBusy] = useState(false);
  // iter212 — View Applied Entry (read-only summary of what landed on payroll).
  const [appliedTarget, setAppliedTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await axios.get(`${API}/admin/time-edits${params}`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (id) => {
    try {
      await axios.put(`${API}/admin/time-edits/${id}`, { status: 'approved' }, { withCredentials: true });
      toast.success('Edit approved — payroll updated');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve');
    }
  };

  const openDecline = (item) => {
    setDeclineTarget(item);
    setDeclineReason('');
    setDeclineOpen(true);
  };

  const submitDecline = async () => {
    if (!declineReason.trim()) {
      toast.error('Decline reason is required');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/time-edits/${declineTarget.id}`,
        { status: 'rejected', decline_reason: declineReason.trim() },
        { withCredentials: true },
      );
      toast.success('Edit declined — employee notified');
      setDeclineOpen(false);
      setDeclineTarget(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to decline');
    }
  };

  const openRepair = async () => {
    setRepairOpen(true);
    setRepairData(null);
    setRepairLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/time-edits/repair/preview`, { withCredentials: true });
      setRepairData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load repair preview');
      setRepairOpen(false);
    }
    setRepairLoading(false);
  };

  const applyRepair = async () => {
    setRepairApplying(true);
    try {
      const { data } = await axios.post(`${API}/admin/time-edits/repair/apply`, {}, { withCredentials: true });
      toast.success(`Repaired ${data.repaired} entr${data.repaired === 1 ? 'y' : 'ies'}${data.blocked ? ` · ${data.blocked} blocked` : ''}`);
      setRepairOpen(false);
      setRepairData(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Repair failed');
    }
    setRepairApplying(false);
  };

  // iter212 — ISO → <input type="datetime-local"> value (browser-local, matches
  // how the employee dialog captures time; converted back to ISO on submit).
  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };

  // iter212 — classify an approved request by whether its payroll change landed.
  const approvedStatus = (it) => {
    if (it.status !== 'approved') return null;
    const ci = it.proposed_clock_in ? new Date(it.proposed_clock_in) : null;
    const co = it.proposed_clock_out ? new Date(it.proposed_clock_out) : null;
    if (ci && co && co <= ci) return 'needs_correction';
    if (it.materialized_entry_id) return 'applied';
    return 'blocked';
  };

  const openCorrect = (it) => {
    setCorrectTarget(it);
    setCorrectForm({
      clock_in: toLocalInput(it.proposed_clock_in),
      clock_out: toLocalInput(it.proposed_clock_out),
      break_hours: it.break_hours != null ? String(it.break_hours) : '',
      reason: '',
    });
    setCorrectOpen(true);
  };

  const correctHoursPreview = () => {
    const ci = correctForm.clock_in ? new Date(correctForm.clock_in) : null;
    const co = correctForm.clock_out ? new Date(correctForm.clock_out) : null;
    if (!ci || !co || co <= ci) return null;
    const brk = Number(correctForm.break_hours) || 0;
    return Math.max(0, (co - ci) / 3600000 - brk);
  };

  const submitCorrect = async () => {
    if (!correctForm.reason.trim()) { toast.error('A correction reason is required.'); return; }
    if (!correctForm.clock_in || !correctForm.clock_out) { toast.error('Clock-in and clock-out are required.'); return; }
    const ci = new Date(correctForm.clock_in), co = new Date(correctForm.clock_out);
    if (co <= ci) { toast.error('Clock-out is earlier than clock-in. Review AM/PM.'); return; }
    setCorrectBusy(true);
    try {
      const body = {
        proposed_clock_in: ci.toISOString(),
        proposed_clock_out: co.toISOString(),
        reason: correctForm.reason.trim(),
      };
      if (correctForm.break_hours !== '') body.break_hours = Number(correctForm.break_hours);
      const { data } = await axios.post(`${API}/admin/time-edits/${correctTarget.id}/correct`, body, { withCredentials: true });
      toast.success(`Corrected — ${data.after.hours}h applied on ${data.after.work_date}`);
      setCorrectOpen(false);
      setCorrectTarget(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Correction failed');
    }
    setCorrectBusy(false);
  };

  return (
    <div data-testid="edit-requests-tab" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <Button
            key={f}
            data-testid={`edit-req-filter-${f}`}
            size="sm"
            onClick={() => setFilter(f)}
            variant={filter === f ? 'default' : 'outline'}
            className={filter === f
              ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold'
              : 'border-[#262626] text-white hover:bg-[#262626]'}
          >
            {f.toUpperCase()}
          </Button>
        ))}
        <div className="flex-1" />
        <Button
          data-testid="edit-req-repair-btn"
          size="sm"
          variant="outline"
          onClick={openRepair}
          className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold"
          title="Find approved requests that never created a time entry, and backfill them"
        >
          <Wrench className="w-3.5 h-3.5 mr-1.5" /> Repair Approved
        </Button>
      </div>

      {loading && <p className="text-[#A3A3A3] text-sm">Loading…</p>}

      {!loading && items.length === 0 && (
        <div data-testid="edit-req-empty" className="bg-[#141414] border border-[#262626] rounded p-6 text-center text-[#A3A3A3] text-sm">
          No {filter === 'all' ? '' : filter} edit requests.
        </div>
      )}

      <div className="space-y-2">
        {items.map((it) => {
          const canDecide = it.status === 'pending' && it.user_id !== currentUserId;
          const isSelf = it.user_id === currentUserId;
          return (
            <div key={it.id} data-testid={`edit-req-${it.id}`} className="bg-[#141414] border border-[#262626] rounded p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="w-4 h-4 text-[#D4AF37]" />
                    <p className="text-white font-bold text-sm">{it.user_name || 'Employee'}</p>
                    <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] bg-[#262626] px-1.5 py-0.5 rounded">
                      {it.request_type === 'missing' ? 'Missing entry' : 'Edit'}
                    </span>
                    <StatusChip status={it.status || 'pending'} />
                    {(() => {
                      const as = approvedStatus(it);
                      if (!as) return null;
                      const meta = {
                        applied: { t: 'Applied', c: 'text-green-300 bg-green-500/15 border-green-500/40' },
                        blocked: { t: 'Blocked', c: 'text-amber-200 bg-amber-500/15 border-amber-500/40' },
                        needs_correction: { t: 'Needs Correction', c: 'text-red-300 bg-red-500/15 border-red-500/50' },
                      }[as];
                      return (
                        <span data-testid={`applied-chip-${it.id}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded ${meta.c}`}>
                          {meta.t}
                        </span>
                      );
                    })()}
                    {it.payroll_week_start && (
                      <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]/70 bg-[#D4AF37]/10 px-1.5 py-0.5 rounded">
                        Wk {it.payroll_week_start}
                      </span>
                    )}
                    {isSelf && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">
                        Yours — cannot self-approve
                      </span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2">
                      <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px] font-bold mb-1">Current</p>
                      <p className="text-white">
                        In: <span className="font-mono">{fmtT(it.current_clock_in)}</span>
                      </p>
                      <p className="text-white">
                        Out: <span className="font-mono">{fmtT(it.current_clock_out)}</span>
                      </p>
                    </div>
                    <div className="bg-[#1a1505] border border-[#D4AF37]/40 rounded p-2">
                      <p className="text-[#D4AF37] uppercase tracking-wider text-[10px] font-bold mb-1">Requested</p>
                      <p className="text-white">
                        In: <span className="font-mono">{fmtT(it.proposed_clock_in)}</span>
                      </p>
                      <p className="text-white">
                        Out: <span className="font-mono">{fmtT(it.proposed_clock_out)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 text-xs">
                    <p className="text-[#A3A3A3]">
                      Category: <span className="text-white">{REASON_LABELS[it.reason_category] || it.reason_category || '—'}</span>
                    </p>
                    <p className="text-white mt-0.5">
                      <span className="text-[#A3A3A3]">Reason:</span> {it.reason || '—'}
                    </p>
                    <p className="text-[#525252] mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Submitted {fmtDT(it.created_at)}
                    </p>
                    {it.status === 'rejected' && it.decline_reason && (
                      <p className="text-red-300 mt-1 border-l-2 border-red-500/40 pl-2">Declined: {it.decline_reason}</p>
                    )}
                    {it.status === 'approved' && it.decided_at && (
                      <p className="text-green-300 mt-1">
                        Approved by {it.decided_by_name || 'admin'} · {fmtDT(it.decided_at)}
                      </p>
                    )}
                  </div>
                </div>
                {canDecide && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      data-testid={`edit-req-approve-${it.id}`}
                      size="sm"
                      onClick={() => approve(it.id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      data-testid={`edit-req-decline-${it.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => openDecline(it)}
                      className="border-[#7F1D1D] text-red-300 hover:bg-[#7F1D1D] hover:text-white"
                    >
                      <X className="w-4 h-4 mr-1" /> Decline
                    </Button>
                  </div>
                )}
                {it.status === 'approved' && (
                  <div className="flex flex-col gap-2 shrink-0">
                    {it.materialized_entry_id && approvedStatus(it) === 'applied' && (
                      <Button
                        data-testid={`view-applied-${it.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => setAppliedTarget(it)}
                        className="border-[#262626] text-white hover:bg-[#262626]"
                      >
                        <Clock className="w-4 h-4 mr-1" /> View Applied Entry
                      </Button>
                    )}
                    <Button
                      data-testid={`edit-approved-${it.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => openCorrect(it)}
                      className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold"
                    >
                      <Wrench className="w-4 h-4 mr-1" /> Edit Approved Request
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid="edit-req-decline-dialog" className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">Decline Edit Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[#A3A3A3] text-sm">
              This will notify <span className="text-white font-bold">{declineTarget?.user_name || 'the employee'}</span> and keep their timesheet unchanged.
            </p>
            <div>
              <Label className="text-white">Decline reason *</Label>
              <Textarea
                data-testid="edit-req-decline-reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={3}
                className="bg-[#0A0A0A] border-[#262626] text-white"
                placeholder="Explain why you're declining this request…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)} className="border-[#262626] text-white hover:bg-[#262626]">
              Cancel
            </Button>
            <Button
              data-testid="edit-req-decline-submit"
              onClick={submitDecline}
              disabled={!declineReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter211 — Repair Approved dialog: preview then backfill. */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent data-testid="edit-req-repair-dialog" className="bg-[#141414] border-[#262626] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2"><Wrench className="w-4 h-4" /> Repair Approved Requests</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[#A3A3A3] text-sm">
              These requests are marked <span className="text-green-300 font-bold">approved</span> but never created the underlying time entry. Review before backfilling — this creates the missing entries and refreshes payroll.
            </p>
            {repairLoading && <p className="text-[#A3A3A3] text-sm">Scanning approved requests…</p>}
            {!repairLoading && repairData && (
              <>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-white font-bold" data-testid="repair-count">{repairData.count} gap(s)</span>
                  <span className="text-green-300" data-testid="repair-repairable">{repairData.repairable} repairable</span>
                  {repairData.count - repairData.repairable > 0 && (
                    <span className="text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {repairData.count - repairData.repairable} blocked</span>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {(repairData.gaps || []).length === 0 ? (
                    <p className="text-[#A3A3A3] text-sm" data-testid="repair-empty">No approved requests need repair — payroll is consistent.</p>
                  ) : repairData.gaps.map((g) => (
                    <div key={g.edit_id} data-testid={`repair-row-${g.edit_id}`} className={`border rounded p-2.5 text-sm ${g.blocked ? 'border-amber-500/40 bg-amber-500/[0.05]' : 'border-[#262626] bg-[#0A0A0A]'}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-white font-bold">{g.user_name || 'Employee'}</span>
                        <span className="text-[#D4AF37] font-bold">{g.hours}h · {g.work_date || '—'}</span>
                      </div>
                      <p className="text-[#A3A3A3] text-xs mt-0.5">
                        {fmtT(g.proposed_clock_in)} → {fmtT(g.proposed_clock_out)} · wk {g.payroll_week_start || '—'} · {g.request_type}
                      </p>
                      {g.blocked ? (
                        <p className="text-amber-300 text-xs mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Blocked — {g.block_reason || 'original entry no longer exists; correct manually.'}</p>
                      ) : (
                        <p className="text-green-300 text-xs mt-1">Will {g.action === 'reapply_patch' ? 're-apply the approved edit' : 'create the missing time entry'}.</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairOpen(false)} className="border-[#262626] text-white hover:bg-[#262626]">Close</Button>
            <Button
              data-testid="repair-apply-btn"
              onClick={applyRepair}
              disabled={repairApplying || repairLoading || !repairData || repairData.repairable === 0}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold disabled:opacity-40"
            >
              {repairApplying ? 'Repairing…' : `Backfill ${repairData?.repairable || 0} Entr${(repairData?.repairable || 0) === 1 ? 'y' : 'ies'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter212 — Edit Approved Request (correct a typo; preserves audit). */}
      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent data-testid="edit-approved-dialog" className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2"><Wrench className="w-4 h-4" /> Edit Approved Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[#A3A3A3] text-sm">
              Correcting <span className="text-white font-bold">{correctTarget?.user_name || 'the employee'}</span>&apos;s approved request. The employee&apos;s original submission is preserved in the audit trail and the applied time entry is updated in place — no duplicate.
            </p>
            {/* Before / after preview */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2">
                <p className="text-[#A3A3A3] uppercase tracking-wider text-[10px] font-bold mb-1">Before (applied)</p>
                <p className="text-white font-mono">{fmtT(correctTarget?.proposed_clock_in)}</p>
                <p className="text-white font-mono">{fmtT(correctTarget?.proposed_clock_out)}</p>
              </div>
              <div className="bg-[#1a1505] border border-[#D4AF37]/40 rounded p-2">
                <p className="text-[#D4AF37] uppercase tracking-wider text-[10px] font-bold mb-1">After</p>
                <p className="text-white font-mono">{correctForm.clock_in ? fmtT(new Date(correctForm.clock_in).toISOString()) : '—'}</p>
                <p className="text-white font-mono">{correctForm.clock_out ? fmtT(new Date(correctForm.clock_out).toISOString()) : '—'}</p>
                <p className="text-[#D4AF37] font-bold mt-1" data-testid="correct-hours-preview">
                  {correctHoursPreview() != null ? `${correctHoursPreview().toFixed(2)}h paid` : 'Invalid times'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-white text-xs">Clock-In *</Label>
                <input data-testid="correct-clock-in" type="datetime-local" value={correctForm.clock_in} onChange={(e) => setCorrectForm({ ...correctForm, clock_in: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-9 px-2 text-sm mt-1" />
              </div>
              <div>
                <Label className="text-white text-xs">Clock-Out *</Label>
                <input data-testid="correct-clock-out" type="datetime-local" value={correctForm.clock_out} onChange={(e) => setCorrectForm({ ...correctForm, clock_out: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-9 px-2 text-sm mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-white text-xs">Break (hours)</Label>
              <input data-testid="correct-break" type="number" step="0.25" min="0" value={correctForm.break_hours} onChange={(e) => setCorrectForm({ ...correctForm, break_hours: e.target.value })} placeholder="0" className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-9 px-2 text-sm mt-1" />
            </div>
            <div>
              <Label className="text-white text-xs">Correction reason *</Label>
              <Textarea data-testid="correct-reason" value={correctForm.reason} onChange={(e) => setCorrectForm({ ...correctForm, reason: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="e.g. AM/PM typo — was 8:15 PM, should be 8:15 AM" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectOpen(false)} className="border-[#262626] text-white hover:bg-[#262626]">Cancel</Button>
            <Button
              data-testid="correct-submit"
              onClick={submitCorrect}
              disabled={correctBusy || !correctForm.reason.trim() || correctHoursPreview() == null}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold disabled:opacity-40"
            >
              {correctBusy ? 'Saving…' : 'Save Correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter212 — View Applied Entry (read-only summary of what's on payroll). */}
      <Dialog open={!!appliedTarget} onOpenChange={(o) => { if (!o) setAppliedTarget(null); }}>
        <DialogContent data-testid="applied-entry-dialog" className="bg-[#141414] border-[#262626] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2"><Clock className="w-4 h-4" /> Applied Time Entry</DialogTitle>
          </DialogHeader>
          {appliedTarget && (
            <div className="space-y-2 text-sm">
              <p className="text-white font-bold">{appliedTarget.user_name}</p>
              <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3 space-y-1">
                <p className="text-white">In: <span className="font-mono">{fmtT(appliedTarget.proposed_clock_in)}</span></p>
                <p className="text-white">Out: <span className="font-mono">{fmtT(appliedTarget.proposed_clock_out)}</span></p>
                {appliedTarget.payroll_week_start && <p className="text-[#A3A3A3] text-xs">Payroll week {appliedTarget.payroll_week_start}</p>}
                <p className="text-[#525252] text-[11px] break-all">Entry ID: {appliedTarget.materialized_entry_id}</p>
              </div>
              <p className="text-green-300 text-xs">This entry is live on Daily, Weekly, My Timesheet, and Payroll.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAppliedTarget(null)} className="border-[#262626] text-white hover:bg-[#262626]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditRequestsTab;
